import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ClipboardList,
  FileText,
  FolderSearch,
} from "lucide-react";
import { Iso2700127701Hub } from "../components/Iso2700127701Hub";
import { Iso2700127701SoaSection } from "../sections/soa/components/Iso2700127701SoaSection";
import { Iso2700127701DocumentosSistemaSection } from "../sections/documentos-sistema/components/Iso2700127701DocumentosSistemaSection";
import { Iso2700127701InventarioDispositivosSection } from "../sections/inventario-dispositivos/components/Iso2700127701InventarioDispositivosSection";
import { Iso2700127701InventarioSoftwareSection } from "../sections/inventario-software/components/Iso2700127701InventarioSoftwareSection";
import { Iso2700127701ControlesInformaticaSection } from "../sections/controles-informatica/components/Iso2700127701ControlesInformaticaSection";
import { ManualSistemaIntegradoSection } from "../../../shared/manual-sistema-integrado/components/ManualSistemaIntegradoSection";
import { IndicadoresSection } from "../../../shared/indicadores/components/IndicadoresSection";
import { IsoCertificateActions } from "../../../shared/certificados-iso/components/IsoCertificateActions";
import { IsoCertificatePreviewModal } from "../../../shared/certificados-iso/components/IsoCertificatePreviewModal";
import { IsoCertificateUploadModal } from "../../../shared/certificados-iso/components/IsoCertificateUploadModal";
import { useIsoCertificates } from "../../../shared/certificados-iso/hooks/useIsoCertificates";
import type { IsoCertificateSlot } from "../../../shared/certificados-iso/types/certificados-iso";
import { CertificationStandardHero } from "../../../shared/ui";
import { useCanManageCertificaciones } from "../../../shared/permissions/useCertificationPermissions";
import { useGlobalNavigationTarget } from "@shared/navigation/globalNavigation";
import "../../../styles/certificaciones.css";
import "../styles/iso-27001-27701.css";
import "../styles/operational-sections.css";
import "../../iso-9001-14001/styles/iso-9001-14001.css";

type Section = "hub" | "manual-sistema-integrado" | "indicadores" | "soa" | "documentos-sistema" | "inventario-dispositivos" | "inventario-software" | "controles-informatica";

const SECTION_LABELS: Record<Section, string> = {
  hub: "Inicio",
  "manual-sistema-integrado": "Manual del Sistema Integrado",
  indicadores: "Indicadores",
  soa: "SOA",
  "documentos-sistema": "Documentos",
  "inventario-dispositivos": "BG02",
  "inventario-software": "BG03",
  "controles-informatica": "BG04",
};

export function Iso2700127701Page() {
  const canManageCertificaciones = useCanManageCertificaciones();
  const navigate = useNavigate();
  const certificates = useIsoCertificates();
  const [previewCertificate, setPreviewCertificate] = useState<IsoCertificateSlot | null>(null);
  const [uploadCertificate, setUploadCertificate] = useState<IsoCertificateSlot | null>(null);
  const [section, setSection] = useState<Section>("hub");

  useGlobalNavigationTarget("cert-iso-27001-27701", (target) => {
    switch (target.section) {
      case "hub":
      case "manual-sistema-integrado":
      case "indicadores":
      case "soa":
      case "documentos-sistema":
      case "inventario-dispositivos":
      case "inventario-software":
      case "controles-informatica":
        setSection(target.section);
        break;
      default:
        break;
    }
  });

  return (
    <div className="page-shell calidad-page-shell" data-certificaciones-readonly={canManageCertificaciones ? "false" : "true"}>
      {section === "hub" ? (
        <CertificationStandardHero
          title="ISO 27001-27701"
          description="Gestiona el sistema de seguridad de la información y privacidad con la declaración de aplicabilidad, el Plan Anexo A y la documentación operativa del SGSI."
          secondaryDescription="Este estándar mantiene sus secciones y registros separados de ISO 9001-14001 dentro del apartado Certificaciones."
          stats={[
            { icon: ClipboardList, label: "Secciones", value: "8 activas" },
            { icon: FolderSearch, label: "Vista", value: SECTION_LABELS.hub },
            { icon: FileText, label: "Contenido", value: "24 tablas" },
          ]}
          actions={
            <IsoCertificateActions
              standard="iso-27001-27701"
              slots={certificates.slots}
              loading={certificates.loading}
              canManage={certificates.canManage}
              variant="hero"
              onPreview={setPreviewCertificate}
              onUpload={(slot) => {
                certificates.clearError();
                setUploadCertificate(slot);
              }}
            />
          }
          onBack={() => void navigate({ to: "/certificaciones" })}
        />
      ) : null}

      <section className="certificaciones-standard-detail dashboard-fade-up">
        {section === "hub" && (
          <Iso2700127701Hub
            onOpenManual={() => setSection("manual-sistema-integrado")}
            onOpenIndicadores={() => setSection("indicadores")}
            onOpenBiblioteca={() => void navigate({ to: "/certificaciones/iso-27001-27701/biblioteca" })}
            onOpenSoa={() => setSection("soa")}
            onOpenDocumentosSistema={() => setSection("documentos-sistema")}
            onOpenInventarioDispositivos={() => setSection("inventario-dispositivos")}
            onOpenInventarioSoftware={() => setSection("inventario-software")}
            onOpenControlesInformatica={() => setSection("controles-informatica")}
          />
        )}

        {section === "manual-sistema-integrado" && (
          <ManualSistemaIntegradoSection standard="iso-27001-27701" onBack={() => setSection("hub")} />
        )}
        {section === "indicadores" && (
          <IndicadoresSection standard="iso-27001-27701" onBack={() => setSection("hub")} />
        )}
        {section === "soa" && <Iso2700127701SoaSection onBack={() => setSection("hub")} />}
        {section === "documentos-sistema" && <Iso2700127701DocumentosSistemaSection onBack={() => setSection("hub")} />}
        {section === "inventario-dispositivos" && <Iso2700127701InventarioDispositivosSection onBack={() => setSection("hub")} />}
        {section === "inventario-software" && <Iso2700127701InventarioSoftwareSection onBack={() => setSection("hub")} />}
        {section === "controles-informatica" && <Iso2700127701ControlesInformaticaSection onBack={() => setSection("hub")} />}
      </section>

      <IsoCertificatePreviewModal
        slot={previewCertificate}
        onClose={() => setPreviewCertificate(null)}
      />

      <IsoCertificateUploadModal
        slot={uploadCertificate}
        uploadingCode={certificates.uploadingCode}
        error={certificates.error}
        onClose={() => {
          certificates.clearError();
          setUploadCertificate(null);
        }}
        onSubmit={async (slot, file) => {
          const saved = await certificates.upload(slot.codigo, file);
          if (saved) {
            setUploadCertificate(null);
          }
        }}
      />
    </div>
  );
}
